/* ============================================================================
 * Price Book — per-store, item-level price memory (Phase 1: capture).
 *
 * The receipt scanner already itemizes every receipt; these helpers file
 * those line items into `price_book_entry` (migration 055) instead of
 * discarding them. Append-only history: prices change, so "latest per item"
 * is always a query, never an update.
 *
 * Trust doctrine: a receipt line amount may cover multiple units, so scan
 * rows carry is_unit_price=false — only sources that STATE a per-unit price
 * (contractor statement, supplier quote) may set it true. Later phases let
 * PQ read the book in takeoffs and write entries from chat.
 * ========================================================================== */
import { supabase } from '@/lib/supabaseClient';

export const addPriceBookEntries = async (entries) => {
  const rows = (entries || [])
    .filter((e) => e && String(e.item || '').trim() && Number(e.price) > 0)
    .map((e) => ({
      store: String(e.store || 'Other').trim().slice(0, 120),
      item: String(e.item).trim().slice(0, 300),
      price: Math.round(Number(e.price) * 100) / 100,
      is_unit_price: !!e.isUnitPrice,
      unit: e.unit ? String(e.unit).slice(0, 20) : null,
      qty: Number.isFinite(Number(e.qty)) && e.qty !== null && e.qty !== ''
        ? Number(e.qty)
        : null,
      source: e.source || 'manual',
      source_ref: e.sourceRef ? String(e.sourceRef).slice(0, 120) : null,
      ...(e.capturedAt ? { captured_at: e.capturedAt } : {}),
      notes: e.notes ? String(e.notes).slice(0, 300) : null,
    }));
  if (rows.length === 0) return 0;
  const { error } = await supabase.from('price_book_entry').insert(rows);
  if (error) throw error;
  return rows.length;
};

/**
 * Best-effort capture from a receipt-scan save. Must NEVER block or fail the
 * receipt save itself — the book is a bonus, the receipt is the record.
 * Returns the number of entries filed (0 on any failure).
 */
export const capturePriceBookFromScan = async ({ store, date, items, sourceRef }) => {
  try {
    return await addPriceBookEntries(
      (items || []).map((it) => ({
        store,
        item: it.desc,
        price: it.amount,
        capturedAt: date || undefined,
        source: 'receipt_scan',
        sourceRef,
        isUnitPrice: false,
        notes: 'Receipt line — amount may cover multiple units',
      }))
    );
  } catch (e) {
    console.warn('[pricebook] capture skipped:', e?.message);
    return 0;
  }
};
